import { LayerId } from "fond/types";

import { formatList, Gate } from "./gating";
import { BASE_DATA_LAYERS, BaseDataLayerKey, BaseDataMissingLayers, BaseDataPresence, GenerationKey } from "./layers";
import { Staleness } from "./staleness";

export type BaseDataRowStatus = "done" | "partial" | "required" | "ready" | "unavailable" | "stale" | "loading" | "running" | "queued" | "empty";

export type BaseDataRowState = {
  status: BaseDataRowStatus;
  caption: string;
  isNext: boolean;
};

export type BaseDataRowInput = {
  key: BaseDataLayerKey;
  present: BaseDataPresence;
  missing: BaseDataMissingLayers;
  staleness: Staleness;
  /** Whether each generation workflow can run now (see `BaseDataGating.generate`). */
  generate: Record<GenerationKey, Gate>;
  /** What each generation workflow would generate its layer from (see `BaseDataGating.generatedFrom`). */
  generatedFrom: Record<GenerationKey, string>;
  /** The layer the user should add next (see `BaseDataGating.nextKey`). */
  nextKey: BaseDataLayerKey | null;
  /** Whether an upload or area select into this layer is in flight. */
  loading: boolean;
  /** Generation workflows currently starting or in progress. */
  running: readonly GenerationKey[];
  /** Generation workflows waiting for a running one to finish before they start. */
  queued: readonly GenerationKey[];
  /** Number of features in the layer, if known. */
  count?: number | null;
};

const isGenerationKey = (key: BaseDataLayerKey): key is GenerationKey => key === "ug" || key === "leadins";

const isStale = (key: BaseDataLayerKey, { ugStale, leadinsStale }: Staleness): boolean =>
  (key === "ug" && ugStale) || (key === "leadins" && leadinsStale);

const countCaption = (key: BaseDataLayerKey, count: number | null | undefined): string => {
  if (count === null || count === undefined) return "Added";
  const [singular, plural] = BASE_DATA_LAYERS[key].units;
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`;
};

const partialCaption = (key: BaseDataLayerKey, missing: readonly LayerId[], count: number | null | undefined): string => {
  const { layerIds, layerNames = [] } = BASE_DATA_LAYERS[key];
  const missingNames = formatList(layerIds.flatMap((layerId, index) => (missing.includes(layerId) ? [layerNames[index]] : [])));
  if (count !== null && count !== undefined && !missing.includes(layerIds[0])) return `${countCaption(key, count)} · ${missingNames} missing`;
  return `${missingNames.charAt(0).toUpperCase()}${missingNames.slice(1)} missing`;
};

const getStatusAndCaption = ({
  key,
  present,
  missing,
  staleness,
  generate,
  generatedFrom,
  loading,
  running,
  queued,
  count,
}: BaseDataRowInput): Pick<BaseDataRowState, "status" | "caption"> => {
  const definition = BASE_DATA_LAYERS[key];
  if (loading) return { status: "loading", caption: "Loading…" };
  if (isGenerationKey(key) && running.includes(key)) return { status: "running", caption: "Generating…" };
  if (isGenerationKey(key) && queued.includes(key)) return { status: "queued", caption: "Waiting to generate" };
  if (present[key] && isStale(key, staleness)) return { status: "stale", caption: "Out of date" };
  if (present[key]) return { status: "done", caption: countCaption(key, count) };
  if (missing[key].length < definition.layerIds.length) return { status: "partial", caption: partialCaption(key, missing[key], count) };
  if (definition.group === "inputs") return { status: "required", caption: "Required" };
  if (isGenerationKey(key)) {
    const gate = generate[key];
    return gate.enabled ? { status: "ready", caption: generatedFrom[key] } : { status: "unavailable", caption: gate.reason };
  }
  if (key === "boundary") return { status: "empty", caption: "Optional · import data from it" };
  return { status: "empty", caption: "Optional" };
};

/** Return the status, caption and NEXT badge of a base data row. */
export const getBaseDataRowState = (input: BaseDataRowInput): BaseDataRowState => {
  const { status, caption } = getStatusAndCaption(input);
  return {
    status,
    caption,
    isNext: input.nextKey === input.key && status !== "loading" && input.running.length === 0,
  };
};
